/**
 * Unified analytics event dispatcher.
 *
 * All conversion and engagement events go through this module.
 * Internally dispatches to GA4 (gtag), Google Ads, Meta Pixel (fbq),
 * and TikTok Pixel (ttq) — each gated by the user's consent choices.
 */
import { getConsent } from "./consent";

// ── Helpers ──────────────────────────────────────────────────────────────────

function ga(event: string, params?: Record<string, unknown>) {
  if (!getConsent().analytics) return;
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("event", event, params);
}

function gads(label: string, value?: number, currency = "EUR") {
  if (!getConsent().marketing) return;
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  const id = import.meta.env.VITE_GOOGLE_ADS_ID as string | undefined;
  if (!id) return;
  window.gtag("event", "conversion", {
    send_to: `${id}/${label}`,
    ...(value != null ? { value, currency } : {}),
  });
}

function meta(event: string, params?: Record<string, unknown>) {
  if (!getConsent().marketing) return;
  if (typeof window === "undefined" || !window.fbq) return;
  window.fbq("track", event, params);
}

function metaCustom(event: string, params?: Record<string, unknown>) {
  if (!getConsent().marketing) return;
  if (typeof window === "undefined" || !window.fbq) return;
  window.fbq("trackCustom", event, params);
}

function tiktok(event: string, params?: Record<string, unknown>) {
  if (!getConsent().marketing) return;
  if (typeof window === "undefined" || !window.ttq) return;
  window.ttq.track(event, params);
}

// ── Item types ───────────────────────────────────────────────────────────────

export interface AnalyticsItem {
  item_id?: string;
  item_name: string;
  item_category?: string;
  price?: number;
  quantity?: number;
}

// ── Funnel events ────────────────────────────────────────────────────────────

/** Pricing section scrolls into view on the landing page. */
export function trackViewItemList(items: AnalyticsItem[]) {
  ga("view_item_list", { item_list_id: "pricing", item_list_name: "Pricing", items });
  meta("ViewContent", { content_type: "product_group" });
  tiktok("ViewContent", { content_type: "product_group" });
}

/** User clicks a "Build your event" CTA on the landing page. */
export function trackSelectItem(tierName: string) {
  ga("select_item", {
    item_list_id: "pricing",
    item_list_name: "Pricing",
    items: [{ item_name: tierName }],
  });
  meta("Lead", { content_name: tierName, content_category: "tier_select" });
  tiktok("ClickButton", { content_name: tierName });
}

/** User adds a tier or addon to their builder selection. */
export function trackAddToCart(item: AnalyticsItem, value: number, currency = "EUR") {
  ga("add_to_cart", { currency, value, items: [item] });
  meta("AddToCart", { content_name: item.item_name, value, currency });
  tiktok("AddToCart", { content_name: item.item_name, value, currency });
}

/** User removes an addon from their builder selection. */
export function trackRemoveFromCart(item: AnalyticsItem, value: number, currency = "EUR") {
  ga("remove_from_cart", { currency, value, items: [item] });
}

/** User enters the builder page or submits final checkout. */
export function trackBeginCheckout(
  value: number,
  currency = "EUR",
  items: AnalyticsItem[] = [],
  step?: "build" | "submit",
) {
  ga("begin_checkout", { currency, value, items, checkout_step: step });
  meta("InitiateCheckout", { value, currency, num_items: items.length });
  tiktok("InitiateCheckout", { value, currency });
}

/** User reaches the contact/shipping step. */
export function trackAddShippingInfo(value: number, currency = "EUR") {
  ga("add_shipping_info", { currency, value });
}

/** Order successfully completed (purchase or free order). */
export function trackPurchase(
  transactionId: string,
  value: number,
  currency = "EUR",
  items: AnalyticsItem[] = [],
) {
  ga("purchase", { transaction_id: transactionId, currency, value, items });

  // Google Ads conversion
  const convLabel = import.meta.env.VITE_GOOGLE_ADS_CONVERSION_LABEL as string | undefined;
  if (convLabel) gads(convLabel, value, currency);

  meta("Purchase", { value, currency, content_ids: items.map((i) => i.item_name) });
  tiktok("CompletePayment", { value, currency });
}

/** Upsell addon purchased post-checkout. */
export function trackUpsellPurchase(
  transactionId: string,
  value: number,
  currency = "EUR",
  items: AnalyticsItem[] = [],
) {
  ga("purchase", {
    transaction_id: transactionId,
    currency,
    value,
    items,
    affiliation: "upsell",
  });

  const convLabel = import.meta.env.VITE_GOOGLE_ADS_UPSELL_LABEL as string | undefined;
  if (convLabel) gads(convLabel, value, currency);

  meta("Purchase", { value, currency, content_ids: items.map((i) => i.item_name) });
  tiktok("CompletePayment", { value, currency });
}

// ── Engagement events ────────────────────────────────────────────────────────

export function trackDemoGalleryView() {
  ga("demo_gallery_view");
  meta("ViewContent", { content_name: "demo_gallery", content_type: "product_group" });
  tiktok("ViewContent", { content_name: "demo_gallery" });
}

export function trackPhotoUpload(eventSlug?: string) {
  ga("photo_upload", { event_slug: eventSlug });
  metaCustom("PhotoUpload", { event_slug: eventSlug });
}

export function trackVideoUpload(eventSlug?: string) {
  ga("video_upload", { event_slug: eventSlug });
  metaCustom("VideoUpload", { event_slug: eventSlug });
}

export function trackGalleryEntry(eventSlug: string) {
  ga("gallery_entry", { event_slug: eventSlug });
  meta("ViewContent", { content_name: "event_gallery", content_ids: [eventSlug] });
  tiktok("ViewContent", { content_id: eventSlug, content_name: "event_gallery" });
}

export function trackPromoCodeApplied(code: string) {
  ga("promo_code_applied", { coupon: code });
  metaCustom("PromoCodeApplied", { coupon: code });
}

// ── Generic custom event ─────────────────────────────────────────────────────

export function trackCustom(eventName: string, params?: Record<string, unknown>) {
  ga(eventName, params);
}
